import { tagDirectionOpposite, foundationTagOffset } from "building-generation/utils/foundation";
import type { FoundationConditionTag } from "building-types/foundations/foundationTypes";
import { FOUNDATION_TAG_WIDTH } from "doc/plans/constants";
import { aabb2ByCenterWidthHeight, aabb2Center, aabb2Overlaps, aabb2SetCenter } from "geom/aabb2";
import type { AABB2 } from "geom/aabb2Types";
import { polyLine2PolyLine2DoIntersect } from "geom/intersect";
import { polyLine2EdgeEval } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { rect2ByAABB2 } from "geom/rect2";
import { vec2DistSquared } from "geom/vec2";

const TAG_OVERLAP_WIDTH = FOUNDATION_TAG_WIDTH * 1.3;

interface TagPosition {
  index: number;
  aabb: AABB2;
  tag: FoundationConditionTag;
  line: PolyLine2;
  dx: number;
  dy: number;
  flip?: boolean;
}

/**
 * This function moves foundation tags on their condition line to resolve overlaps between obstacles.
 * @param tags tags to slide
 * @param obstacles additional obstacles for collision
 * @returns a new set of conditions due to readonly nature
 */
export function slideFoundationTags(
  tags: FoundationConditionTag[],
  obstacles: AABB2[] = [],
  obstaclePls: PolyLine2[] = [],
): FoundationConditionTag[] {
  const NUM_STEPS = 20;
  const min = 36;
  const max = 120;
  const delta = max - min;

  const offsets: number[] = [];
  for (let i = 0; i < NUM_STEPS; i++) {
    const t = i / NUM_STEPS;
    const o = min + delta * t;
    offsets.push(o);
    offsets.push(-o);
  }

  const NUM_TS = 5;
  const ts: number[] = [];
  for (let i = 1; i < NUM_TS - 1; i++) {
    const t = i / (NUM_TS * 2);
    ts.push(0.5 + t);
    ts.push(0.5 - t);
  }

  // initialize tags at desired positions
  const tagPositions: TagPosition[] = tags.map((tag, index) => {
    const { x, y, rotation } = tag;
    const [tx, ty] = foundationTagOffset(rotation, min);

    const line = tag.lines[0];
    const dx = x + tx;
    const dy = y + ty;

    const aabbWidth = TAG_OVERLAP_WIDTH + 5;
    const aabb = aabb2ByCenterWidthHeight(dx, dy, aabbWidth, aabbWidth);

    return {
      index,
      aabb,
      tag,
      line,
      dx,
      dy,
    };
  });

  // Find overlaps
  const tagoOverlaps: { [key: string]: TagPosition } = {};
  for (const tag of tagPositions) {
    if (checkForOverlaps(tag, tagPositions, obstacles, obstaclePls)) {
      tagoOverlaps[tag.index] = tag;
    }
  }

  // Sort overlapping conditions by length of condition line.
  const sortedOverlaps = Object.values(tagoOverlaps).sort((a, b) => {
    const aLine = a.line;
    const bLine = b.line;
    const aLen2 = vec2DistSquared(aLine[0], aLine[1], aLine[2], aLine[3]);
    const bLen2 = vec2DistSquared(bLine[0], bLine[1], bLine[2], bLine[3]);
    return aLen2 - bLen2;
  });

  // Move along offset/eval combinations until no collision.
  for (const overlap of sortedOverlaps) {
    const { aabb, tag } = overlap;
    const { rotation } = tag;

    let doesOverlap = checkForOverlaps(overlap, tagPositions, obstacles, obstaclePls); // The conflict could have been solved eariler.
    if (!doesOverlap) {
      continue;
    }

    // prioritize keeping short tags along axis
    // rather than long tags at center
    for (const offset of offsets) {
      if (!doesOverlap) {
        break;
      }

      for (const t of ts) {
        const [x, y] = polyLine2EdgeEval(overlap.line, 0, t, true);
        const [tx, ty] = foundationTagOffset(rotation, offset);

        const dx = x + tx;
        const dy = y + ty;

        aabb2SetCenter(aabb, dx, dy);
        overlap.flip = offset < 0;

        doesOverlap = checkForOverlaps(overlap, tagPositions, obstacles, obstaclePls);
        if (!doesOverlap) {
          break;
        }
      }
    }

    if (!doesOverlap) {
      [overlap.dx, overlap.dy] = aabb2Center(aabb);
    }
  }

  return tagPositions.map((t) => {
    const { flip } = t;
    let direction = t.tag.direction;
    if (flip) {
      direction = tagDirectionOpposite(direction);
    }
    return { ...t.tag, x: t.dx, y: t.dy, direction };
  });
}

function checkForOverlaps(
  toCheck: TagPosition,
  checkAgainst: TagPosition[],
  obstacles: AABB2[],
  obstaclePls: PolyLine2[],
) {
  const { aabb, index } = toCheck;
  for (const potentialOverlap of checkAgainst) {
    if (potentialOverlap === toCheck) {
      continue;
    }
    if (aabb2Overlaps(aabb, potentialOverlap.aabb) && index !== potentialOverlap.index) {
      return true;
    }
  }
  for (const obstacle of obstacles) {
    if (aabb2Overlaps(aabb, obstacle)) {
      return true;
    }
  }
  const rect = rect2ByAABB2(aabb);
  for (const pl of obstaclePls) {
    if (polyLine2PolyLine2DoIntersect(rect, pl)) {
      return true;
    }
  }
  return false;
}
